/**
 * №53 · 53.3 — A MATTER IS A STREAM OF WORK BEING DONE. The reading model.
 *
 * Pure — no React, no fetch. The page's decisions live here so their edges can be
 * asserted: a day with one event, an event with no actor, a chip with no href, a
 * fact whose value the product does not know. None of those appear in the
 * approved drawing (`docs/53-mockups/matters-1.png`), and that is exactly why
 * they are the cases worth testing. 53.4 spent a rung learning this the hard way.
 *
 * ## The rule this file exists to enforce
 *
 * **A fact renders only when the product knows it.** The drawing's rail shows six
 * facts with values; a real matter will be missing several of them for most of its
 * life. D-CR2 rules that a figure the product cannot stand behind renders ABSENT
 * rather than approximate, and R7③ that a section with nothing to say is absent
 * rather than empty. So `factsFor` returns only the facts it has, and the rail is
 * shorter on a thin matter instead of being a column of dashes.
 */
import type { RelatedSummary, StreamEvent } from "../../../lib/types";
import { rowKeyForJoinType } from "./record-subviews";

export interface MatterFact {
  key: string;
  label: string;
  value: string;
  /** The tab that OWNS this fact — the caption's rule: every fact is a link. */
  href: string | null;
}

export interface StreamDay {
  key: string;
  /** "Today" · "Yesterday" · "Friday" · "12 August". */
  label: string;
  events: StreamEvent[];
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

/** `YYYY-MM-DD` from a timestamp string, without ever constructing a zoneless Date. */
export function dayKeyOf(ts: string): string {
  return (ts ?? "").slice(0, 10);
}

/**
 * The day's heading. Today and Yesterday by name; within the last week by weekday
 * (the drawing's "FRIDAY"); older by date. `todayKey` is passed in rather than
 * read from the clock so a gate can pin the day and a frozen render is stable.
 */
export function dayLabel(dayKey: string, todayKey: string): string {
  if (!dayKey) return "Undated";
  if (dayKey === todayKey) return "Today";
  const d = Date.parse(`${dayKey}T00:00:00Z`);
  const t = Date.parse(`${todayKey}T00:00:00Z`);
  if (!Number.isFinite(d) || !Number.isFinite(t)) return dayKey;
  const days = Math.round((t - d) / 86_400_000);
  if (days === 1) return "Yesterday";
  // Only inside the last week — beyond that a weekday name is ambiguous, and
  // "Friday" meaning "Friday three weeks ago" is worse than a date.
  if (days > 1 && days < 7) return DAY_NAMES[new Date(d).getUTCDay()] ?? dayKey;
  const dt = new Date(d);
  return `${dt.getUTCDate()} ${MONTHS[dt.getUTCMonth()] ?? ""}`.trim();
}

/**
 * Group the stream into days, newest first, preserving the server's ordering
 * inside each day. The server already sorted; re-sorting here would be a second
 * opinion about time, and two opinions is how a stream and its panel disagree.
 */
export function groupByDay(events: readonly StreamEvent[], todayKey: string): StreamDay[] {
  const days: StreamDay[] = [];
  const index = new Map<string, StreamDay>();
  for (const e of events) {
    const key = dayKeyOf(e.ts);
    let day = index.get(key);
    if (!day) {
      day = { key, label: dayLabel(key, todayKey), events: [] };
      index.set(key, day);
      days.push(day);
    }
    day.events.push(e);
  }
  return days;
}

/** The clock, read from the string. Never through a Date — the server sends no zone. */
export function clockOf(ts: string): string {
  const m = /(\d{2}):(\d{2})/.exec((ts ?? "").slice(10));
  return m ? `${m[1]}:${m[2]}` : "";
}

export interface MatterFactsInput {
  entityId: string;
  entityType: string;
  feeBasis?: string | null;
  carriedBy?: string | null;
  decides?: string | null;
  /**
   * №53.3.1 S0 (D-CR24/D-CR26) — the record's nearest declared key date
   * currently in its attention window, PRE-FORMATTED exactly as the retired
   * `.recordcap` badge used to read it ("Option exercise 29 Aug" — same
   * `mostUrgentKeyDate` computation, moved here rather than duplicated). The
   * landing view is rechead · lead · tabs · stream+facts and nothing else
   * (decision item 1), so the standalone badge is gone and this is its one
   * surviving fact. Absent when no date is currently in-window — D-CR2, never
   * a stale or past one.
   */
  nextKeyDate?: string | null;
}

/**
 * The facts rail. **Only what the product knows** — see the file header.
 *
 * Each fact carries the route of the tab that owns it, which is the caption's
 * whole rule: *"every fact in the rail is a link to the tab that owns it."* A fact
 * whose owning tab does not exist gets `href: null` and renders as text rather
 * than as a link that goes nowhere — the dead-nav ban applies inside a page too.
 *
 * №53.3.1 S0 (D-CR26 item 2, "the THIRD position on the money question, said
 * out loud") — WIP/estimate/trust money is GONE from here. It never leaves the
 * page: the Billing and Trust Surfaces rows (`surfaceRows`, below) now carry it
 * as a server-composed claim line (`WIP $9,320 of $18,000 …`, `$5,000.00 held
 * …`) — this rail is identity facts only, exactly as the drawing shows it.
 */
export function factsFor(input: MatterFactsInput): MatterFact[] {
  const base = `/app/place/${input.entityType}/${input.entityId}`;
  const out: MatterFact[] = [];
  const fee = (input.feeBasis ?? "").trim();
  if (fee) out.push({ key: "fee", label: "Fee basis", value: fee, href: `${base}/billing` });
  const carried = (input.carriedBy ?? "").trim();
  if (carried) out.push({ key: "carried", label: "Carried by", value: carried, href: `${base}/parties` });
  const decides = (input.decides ?? "").trim();
  // Who decides is a PARTIES fact, and it is deliberately not a link to a person:
  // the tab that owns it is the one that could change it.
  if (decides) out.push({ key: "decides", label: "Decides", value: decides, href: `${base}/parties` });
  const keyDate = (input.nextKeyDate ?? "").trim();
  // Key dates are declared-section data (RecordView) — E-33 moved that section
  // to Details this rung, so the fact links there rather than to a tab that no
  // longer renders it.
  if (keyDate) out.push({ key: "keydate", label: "Next key date", value: keyDate, href: `${base}/details` });
  return out;
}

// ---------------------------------------------------------------------------
// №53.3.1 S0 (D-CR26 items 6 + 4) — THE SURFACES BLOCK.
// ---------------------------------------------------------------------------

export interface SurfaceRow {
  /** The `_meta._related` map key — stable React key, and the server's own
   *  identity for this entry. */
  key: string;
  label: string;
  count: number;
  /** Server-composed, rendered VERBATIM — never edited, never invented. Null
   *  is the honest count-only case (every surface but Trust/Process/Billing,
   *  this rung — D-CR26 item 6). */
  claimText: string | null;
  /** The ONLY red on the page, when true (D-CR26). Always false when
   *  `claimText` is null — a claim-less entry has nothing to be urgent ABOUT. */
  needsPerson: boolean;
  /** The tab this row navigates to, or null when this rung cannot resolve one
   *  (S1's job — see `record-subviews.ts`'s `rowKeyForJoinType`). A row with no
   *  href renders as text, never as a link to nothing (the dead-nav ban). */
  href: string | null;
}

/** The three subject-spine surfaces, in D-CR26's fixed order — never sorted by
 *  count, same rule `record-tab-order.ts` applies to the tab strip itself. */
const SPINE_ROW_KEYS: readonly string[] = ["place-process", "place-record-billing", "place-record-trust"];

/** A matrix row key's own URL segment. Listed rather than imported from
 *  `route-anchor-matrix.ts`: that module pulls in `react-router`, and this
 *  file is deliberately framework-free (see the header — "Pure, no React, no
 *  fetch") so `surfaceRows` stays directly assertable with no DOM. */
const ROW_KEY_SEGMENT: Readonly<Record<string, string>> = {
  "place-record-trust": "trust",
  "place-record-billing": "billing",
  "place-process": "process",
  "place-record-parties": "parties",
  "place-record-files": "files",
  "place-record-register": "register",
  "place-record-conflicts": "conflicts",
};

/** "register entries" → "Register entries" — the join's own prose label,
 *  title-cased for a heading. The three synthesized surfaces already send
 *  Title Case ("Trust", "Billing", "Process"); this only touches the first
 *  character, so it is a no-op for them. */
function titleCase(s: string): string {
  return s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/**
 * `_meta._related`'s entries reduced to `{ rowKey: count }` — the SAME
 * key-resolution `surfaceRows` uses (`entry.key ?? rowKeyForJoinType(entry.type)`),
 * shared so the tab badges and the Surfaces block can never disagree about
 * which entry belongs to which tab. Feeds `useSubViewCounts` in place of the
 * five per-hook count reads it used to take (53.3.1 S0, decision item 5).
 */
export function relatedCountsByRowKey(related: Record<string, RelatedSummary> | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  for (const entry of Object.values(related ?? {})) {
    const rowKey = entry.key ?? rowKeyForJoinType(entry.type);
    if (rowKey) out[rowKey] = (out[rowKey] ?? 0) + entry.count;
  }
  return out;
}

/**
 * One row per `_meta._related` entry, ordered spine-first then joins by count
 * descending. `related` is `EntityRecord._meta._related` straight off the
 * already-fetched `get_row_detail` read — no second query, no client-side
 * claim composition (D-CR2): an entry's `.claim` is rendered if present,
 * dropped if not, and never guessed at here.
 */
export function surfaceRows(
  related: Record<string, RelatedSummary> | undefined,
  entityType: string,
  entityId: string,
): SurfaceRow[] {
  if (!related) return [];
  const base = `/app/place/${entityType}/${entityId}`;

  const withOrder = Object.entries(related).map(([mapKey, entry]) => {
    const rowKey = entry.key ?? rowKeyForJoinType(entry.type);
    const segment = rowKey ? ROW_KEY_SEGMENT[rowKey] : undefined;
    return {
      rowKey,
      row: {
        key: mapKey,
        label: titleCase(entry.label),
        count: entry.count,
        claimText: entry.claim?.text ?? null,
        needsPerson: entry.claim?.needs_person ?? false,
        href: segment ? `${base}/${segment}` : null,
      } satisfies SurfaceRow,
    };
  });

  withOrder.sort((a, b) => {
    const ai = a.rowKey ? SPINE_ROW_KEYS.indexOf(a.rowKey) : -1;
    const bi = b.rowKey ? SPINE_ROW_KEYS.indexOf(b.rowKey) : -1;
    if (ai !== -1 || bi !== -1) {
      if (ai === -1) return 1;   // a is a join, b is spine — b leads
      if (bi === -1) return -1;  // the reverse
      return ai - bi;            // both spine — SPINE_ROW_KEYS' own order
    }
    return b.row.count - a.row.count; // both joins — count descending
  });

  return withOrder.map((e) => e.row);
}

/** Hours per contributor for the "Time this week" block, from the stream's own time events. */
export function timeThisWeek(events: readonly StreamEvent[]): { who: string; hours: number }[] {
  const totals = new Map<string, number>();
  for (const e of events) {
    if (e.kind !== "time" || !e.time) continue;
    const who = e.time.who || "—";
    totals.set(who, (totals.get(who) ?? 0) + e.time.hours);
  }
  return [...totals.entries()]
    .map(([who, hours]) => ({ who, hours: Math.round(hours * 10) / 10 }))
    .filter((r) => r.hours > 0)
    .sort((a, b) => b.hours - a.hours || a.who.localeCompare(b.who));
}

/**
 * The inline action a stream event offers, if any. An open card offers the card;
 * a blocked run offers the nudge. Everything else offers nothing — a stream is a
 * record of what happened, and a button on every line would make it a to-do list.
 */
export function actionFor(e: StreamEvent): { label: string; kind: "nudge" } | null {
  // A card offers NO stream-level action: the page renders the real DecisionCard in
  // its place (D-CC4 / F10 / D-CH2 — see `MatterStream`'s `renderCard`), and the
  // card carries its own affordances. A second "Open the card →" beside it would be
  // a fifth way to answer the same question.
  if (e.kind === "run" && e.termination === "blocked_honest") return { label: "Nudge the client →", kind: "nudge" };
  return null;
}
